/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { handleKeyboardNavigation, moveCaretToFirstVisibleCoordinate, resetCaretMode } from "utils/CaretModeUtils";
import { trackDocOpenedWithCaretMode, trackCaretModeOn, trackCaretModeOff } from "analytics/events";
import { subscribe, unsubscribe } from "utils/keyboard";
import { getShouldScrollToShowCaret } from "selectors/caretModeSelectors";

class CaretModeStore {
  @observable caretMode;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.caretMode = false;
    this.shouldScrollToShowCaret = false;
    this.showCaretOnPDFOpen = false;
  }

  @action
  async setCaretMode(value, isDocOpen = false) {
    this.caretMode = value;
    if (this.caretMode) {
      try {
        await initialPageLoadedPromise(this.stores);
        const shouldJump = getShouldScrollToShowCaret(this.stores);
        if (this.showCaretOnPDFOpen) {
          await moveCaretToFirstVisibleCoordinate(false, shouldJump);
        }
        subscribe(handleKeyboardNavigation);
        isDocOpen ? trackDocOpenedWithCaretMode() : trackCaretModeOn();
      } catch (e) {} // Silent failure in case of error
    } else {
      resetCaretMode();
      unsubscribe(handleKeyboardNavigation);
      if (!isDocOpen) {
        trackCaretModeOff();
      }
    }
  }

  @action
  setShouldScrollToShowCaret(value) {
    this.shouldScrollToShowCaret = value;
  }

  @action
  setShowCaretOnPDFOpen(value) {
    this.showCaretOnPDFOpen = value;
  }
}

export { CaretModeStore };
